import { useRef, useState, useCallback, useEffect } from 'react'

type Phase = 'idle' | 'exit-next' | 'exit-prev' | 'spring-back'

const COMMIT_THRESHOLD_PX = 60
const VELOCITY_THRESHOLD_PX_MS = 0.3
const DIRECTION_LOCK_PX = 8

interface Options {
  onNext: () => void
  onPrev: () => void
}

interface Result {
  scrollBodyRef: React.RefObject<HTMLDivElement>
  wrapperRef: React.RefObject<HTMLDivElement>
  wrapperStyle: React.CSSProperties
  isAnimating: boolean
  triggerNext: () => void
  triggerPrev: () => void
}

export function useCalendarSwipe({ onNext, onPrev }: Options): Result {
  const [phase, setPhase] = useState<Phase>('idle')
  const [offsetX, setOffsetX] = useState(0)

  const phaseRef = useRef<Phase>('idle')
  const onNextRef = useRef(onNext)
  const onPrevRef = useRef(onPrev)
  const scrollBodyRef = useRef<HTMLDivElement>(null)
  const wrapperRef = useRef<HTMLDivElement>(null)

  useEffect(() => { onNextRef.current = onNext }, [onNext])
  useEffect(() => { onPrevRef.current = onPrev }, [onPrev])
  useEffect(() => { phaseRef.current = phase }, [phase])

  const touchRef = useRef<{
    startX: number
    startY: number
    startTime: number
    locked: 'horizontal' | 'vertical' | null
    skip: boolean
  } | null>(null)

  // On transitionend: snap to 0 without re-animating
  useEffect(() => {
    const el = wrapperRef.current
    if (!el) return

    const handleTransitionEnd = () => {
      const p = phaseRef.current
      if (p === 'exit-next' || p === 'exit-prev') {
        // Let React clear transform + transition atomically in the same render
        // as the buffer releasing new content — avoids a flash of old content at 0.
        phaseRef.current = 'idle'
        setPhase('idle')
        setOffsetX(0)
      } else if (p === 'spring-back') {
        phaseRef.current = 'idle'
        setPhase('idle')
      }
    }

    el.addEventListener('transitionend', handleTransitionEnd)
    return () => el.removeEventListener('transitionend', handleTransitionEnd)
  }, [])

  // Imperative touch listeners (passive:false on move so we can preventDefault)
  useEffect(() => {
    const el = scrollBodyRef.current
    if (!el) return

    function handleTouchStart(e: TouchEvent) {
      if (phaseRef.current !== 'idle') return
      if (e.touches.length !== 1) return
      if ((e.target as Element).closest('[data-block]')) {
        touchRef.current = null
        return
      }
      const t = e.touches[0]
      touchRef.current = {
        startX: t.clientX,
        startY: t.clientY,
        startTime: performance.now(),
        locked: null,
        skip: false,
      }
    }

    function handleTouchMove(e: TouchEvent) {
      const touch = touchRef.current
      if (!touch || touch.skip || phaseRef.current !== 'idle') return
      if (e.touches.length !== 1) { touch.skip = true; return }

      const t = e.touches[0]
      const dx = t.clientX - touch.startX
      const dy = t.clientY - touch.startY

      if (!touch.locked) {
        if (Math.abs(dx) < DIRECTION_LOCK_PX && Math.abs(dy) < DIRECTION_LOCK_PX) return
        touch.locked = Math.abs(dx) > Math.abs(dy) ? 'horizontal' : 'vertical'
      }

      if (touch.locked === 'vertical') {
        touch.skip = true
        return
      }

      e.preventDefault()
      setOffsetX(dx)
    }

    function handleTouchEnd(e: TouchEvent) {
      const touch = touchRef.current
      touchRef.current = null

      if (!touch || touch.skip || touch.locked !== 'horizontal' || phaseRef.current !== 'idle') {
        setOffsetX(0)
        return
      }

      const t = e.changedTouches[0]
      const dx = t.clientX - touch.startX
      const dt = performance.now() - touch.startTime
      const velocity = Math.abs(dx) / dt

      const shouldCommit = Math.abs(dx) > COMMIT_THRESHOLD_PX || velocity > VELOCITY_THRESHOLD_PX_MS

      if (!shouldCommit) {
        phaseRef.current = 'spring-back'
        setPhase('spring-back')
        setOffsetX(0)
        return
      }

      // Fire navigation immediately so the fetch starts during the animation.
      // Buffer keeps old content visible while isAnimating=true.
      if (dx < 0) {
        onNextRef.current()
        phaseRef.current = 'exit-next'
        setPhase('exit-next')
      } else {
        onPrevRef.current()
        phaseRef.current = 'exit-prev'
        setPhase('exit-prev')
      }
      setOffsetX(0)
    }

    el.addEventListener('touchstart', handleTouchStart, { passive: true })
    el.addEventListener('touchmove', handleTouchMove, { passive: false })
    el.addEventListener('touchend', handleTouchEnd, { passive: true })
    return () => {
      el.removeEventListener('touchstart', handleTouchStart)
      el.removeEventListener('touchmove', handleTouchMove)
      el.removeEventListener('touchend', handleTouchEnd)
    }
  }, [])

  const triggerNext = useCallback(() => {
    if (phaseRef.current !== 'idle') return
    onNextRef.current()
    phaseRef.current = 'exit-next'
    setPhase('exit-next')
  }, [])

  const triggerPrev = useCallback(() => {
    if (phaseRef.current !== 'idle') return
    onPrevRef.current()
    phaseRef.current = 'exit-prev'
    setPhase('exit-prev')
  }, [])

  const wrapperStyle: React.CSSProperties = (() => {
    // Live drag: follow the finger
    if (phase === 'idle' && offsetX !== 0) return { transform: `translateX(${offsetX}px)` }
    // Commit: slide the entire day area fully off-screen (page-flip)
    if (phase === 'exit-next') return { transform: 'translateX(-100%)', transition: 'transform 220ms ease-out' }
    if (phase === 'exit-prev') return { transform: 'translateX(100%)', transition: 'transform 220ms ease-out' }
    // Spring back to center
    if (phase === 'spring-back') return { transform: 'translateX(0)', transition: 'transform 150ms ease-out' }
    return {}
  })()

  return {
    scrollBodyRef,
    wrapperRef,
    wrapperStyle,
    isAnimating: phase !== 'idle',
    triggerNext,
    triggerPrev,
  }
}
